/**
 * What the Copilot has learned, for an admin to review.
 *
 * WHY THIS SCREEN EXISTS
 *
 * When someone completes a plan for something the agent could not do itself,
 * those steps are remembered org-wide and offered to the next person who asks
 * something similar. That is the point — the discovery happens once.
 *
 * It is also the risk. A workaround that worked for one person becomes the
 * organization's suggested path, and nobody decided that. This is where
 * somebody decides.
 *
 * WHAT AN ADMIN IS ACTUALLY JUDGING
 *
 * Two kinds of evidence, which mean different things and are shown separately:
 *
 *   times completed — weak. It works, or at least it has not visibly failed.
 *   approved        — strong. Somebody says this is how it SHOULD be done.
 *
 * A procedure is suggested once it has either. So this list deliberately
 * includes the ones below the threshold: a review screen showing only what is
 * already being suggested reviews nothing.
 */

import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, ShieldCheck, Trash2, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  approveCopilotProcedure,
  fetchCopilotProcedures,
  retireCopilotProcedure,
  type CopilotProcedure,
} from '@/services/copilotStream';

export default function CopilotProcedureReview() {
  const { t } = useTranslation();
  const [procedures, setProcedures] = useState<CopilotProcedure[]>([]);
  const [minUses, setMinUses] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    const result = await fetchCopilotProcedures(signal);
    if (signal?.aborted) return;
    if ('error' in result) {
      setError(result.error);
      setProcedures([]);
    } else {
      setError(null);
      setProcedures(result.procedures);
      setMinUses(result.min_uses);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const approve = useCallback(
    async (id: string) => {
      setBusyId(id);
      const ok = await approveCopilotProcedure(id);
      setBusyId(null);
      // Re-read rather than patching locally: approving changes whether a
      // procedure is suggested at all, and that is the server's judgement.
      if (ok) await load();
    },
    [load],
  );

  const retire = useCallback(
    async (id: string) => {
      const reason = window.prompt(
        t('copilot.retireReason', 'Why is this being retired? (optional — kept for whoever asks later)') || '',
      );
      // A cancelled prompt returns null; an empty string is a deliberate "no
      // reason given" and should still retire.
      if (reason === null) return;
      setBusyId(id);
      const ok = await retireCopilotProcedure(id, reason || undefined);
      setBusyId(null);
      if (ok) await load();
    },
    [load, t],
  );

  if (loading) {
    return (
      <p className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {t('common.loading', 'Loading…')}
      </p>
    );
  }

  if (error) {
    return <p className="py-8 text-sm text-red-700 dark:text-red-300">{error}</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {t('copilot.procedures', 'What the Copilot has learned')}
        </h3>
        <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
          {t(
            'copilot.proceduresIntro',
            'When someone finishes a set of steps the Copilot suggested, it remembers them and offers them to the next person who asks something similar. Approving one means it is offered straight away; retiring one stops it being offered at all.',
          )}
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('copilot.proceduresThreshold', 'Suggested automatically after')} {minUses}{' '}
          {minUses === 1
            ? t('copilot.proceduresUse', 'successful use')
            : t('copilot.proceduresUses', 'successful uses')}
          {t('copilot.proceduresOrApproval', ', or as soon as it is approved.')}
        </p>
      </div>

      {procedures.length === 0 && (
        <p className="rounded-md border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
          {t('copilot.noProcedures', 'Nothing learned yet. Procedures appear here once someone completes a set of steps the Copilot suggested.')}
        </p>
      )}

      {procedures.map((p) => (
        <div
          key={p.id}
          className={
            'rounded-md border px-4 py-3 ' +
            (p.retired
              ? 'border-gray-200 bg-gray-50 opacity-70 dark:border-gray-700 dark:bg-gray-800/40'
              : 'border-gray-200 dark:border-gray-700')
          }
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.title}</p>
              {/* The request in the asker's own words, which is what a new
                  question is matched against — so it is what tells an admin
                  when this will fire. */}
              <p className="mt-0.5 text-xs italic text-gray-500 dark:text-gray-400">“{p.goal}”</p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {p.retired ? (
                <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {t('copilot.retired', 'Retired')}
                </span>
              ) : p.approved ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-xs text-[#0D9488] dark:bg-teal-900/30">
                  <ShieldCheck className="h-3 w-3" aria-hidden />
                  {t('copilot.approved', 'Approved')}
                </span>
              ) : (
                <span
                  className={
                    'rounded-full px-2 py-0.5 text-xs ' +
                    (p.times_completed >= minUses
                      ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300')
                  }
                >
                  {/* Said plainly, because "used 4 times" and "being
                      suggested to everyone" are different facts and an admin
                      is deciding about the second. */}
                  {p.times_completed >= minUses
                    ? t('copilot.beingSuggested', 'Being suggested')
                    : t('copilot.notYetSuggested', 'Not yet suggested')}
                </span>
              )}
            </div>
          </div>

          <ol className="mt-2 space-y-1">
            {p.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                <span className="mt-0.5 w-4 shrink-0 text-right text-xs text-gray-400">{i + 1}</span>
                {/* A tool step is the agent acting; a manual step is a person.
                    An admin reviewing this needs to know which, because they
                    carry different risk. */}
                {step.kind === 'tool' && (
                  <Wrench className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" aria-hidden />
                )}
                <span className="min-w-0 flex-1">{step.instruction}</span>
              </li>
            ))}
          </ol>

          <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-2 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('copilot.completedTimes', 'Completed')} {p.times_completed}×
              {p.last_used_at ? ` · ${new Date(p.last_used_at).toLocaleDateString()}` : ''}
            </p>

            {!p.retired && (
              <div className="flex items-center gap-2">
                {!p.approved && (
                  <button
                    type="button"
                    onClick={() => void approve(p.id)}
                    disabled={busyId === p.id}
                    className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:border-gray-400 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
                  >
                    <CheckCircle2 className="h-3 w-3" aria-hidden />
                    {t('copilot.approve', 'Approve')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void retire(p.id)}
                  disabled={busyId === p.id}
                  className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-red-700 hover:border-red-400 disabled:opacity-50 dark:border-gray-600 dark:text-red-300"
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                  {t('copilot.retire', 'Retire')}
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
